/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: GPT-5 Codex
 ?? Company: Ring2All LLC
 ?? File: CdrFiltersPage.tsx
 ??? Created: 2025-01-27
 ?? Description:
    Main page for CDR Filters management with list view.
----------------------------------------------------------------*/

import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Filter, Edit, Copy, Trash2 } from 'lucide-react';
import DataGridSimple from '../../components/ui/DataGridSimple';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import { ModulePageContainer } from '../../components/layouts';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNavigationStore } from '../../store/navigation';
import {
  deleteCdrFilterById,
  navigateToCdrFilterDuplicate,
} from './cdrFiltersActions';
import { useCdrFilterList } from './hooks/useCdrFilterList';
import { CdrFilter } from './types';

const ACTION_BUTTON_CLASSES = {
  base: 'p-2 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60',
  edit: 'hover:bg-blue-100 dark:hover:bg-blue-900/30',
  duplicate: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/30',
  delete: 'hover:bg-red-100 dark:hover:bg-red-900/30',
} as const;

export default function CdrFiltersPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();
  const { setCurrentPageTitle } = useNavigationStore();
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    filter: CdrFilter | null;
  }>({
    isOpen: false,
    filter: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const pageTitle = useMemo(() => t('cdrFilters.title'), [t]);

  useEffect(() => {
    setCurrentPageTitle(null);
    return () => setCurrentPageTitle(null);
  }, [setCurrentPageTitle]);

  const {
    data,
    loading,
    columns: baseColumns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useCdrFilterList();

  // Add custom renders to columns
  const columns = useMemo(
    () =>
      baseColumns.map((column) => {
        if (column.key === 'description') {
          return {
            ...column,
            render: (_value: unknown, item: CdrFilter) => (
              <span className="font-medium text-foreground">{item.description}</span>
            )
          };
        }
        if (column.key === 'enabled') {
          return {
            ...column,
            render: (_value: unknown, item: CdrFilter) => (
              <span
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  item.enabled
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
                    : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                }`}
              >
                {item.enabled ? t('common.enabled') : t('common.disabled')}
              </span>
            )
          };
        }
        if (column.key === 'createdAt') {
          return {
            ...column,
            render: (_value: unknown, item: CdrFilter) => (
              <span className="text-sm text-muted-foreground">
                {item.createdAt
                  ? new Date(item.createdAt).toLocaleDateString()
                  : '�'}
              </span>
            )
          };
        }
        return column;
      }),
    [baseColumns, t]
  );

  const handleAdd = useCallback(() => {
    navigate('/reports/cdr/filters/new');
  }, [navigate]);

  const handleEdit = useCallback(
    (filter: CdrFilter) => {
      navigate(`/reports/cdr/filters/${filter.uuid ?? filter.id}`);
    },
    [navigate],
  );

  const handleDuplicate = useCallback(
    (filter: CdrFilter) => {
      navigateToCdrFilterDuplicate(navigate, filter.uuid ?? filter.id);
    },
    [navigate],
  );

  const handleDeleteClick = useCallback((filter: CdrFilter) => {
    setDeleteModal({ isOpen: true, filter });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteModal.filter) return;

    setIsDeleting(true);
    try {
      await deleteCdrFilterById(deleteModal.filter.id);

      showSuccess(t('cdrFilters.messages.deleteSuccess'));
      setDeleteModal({ isOpen: false, filter: null });
      refresh();
    } catch (error) {
      showError(
        error instanceof Error && error.message !== 'DELETE_FAILED'
          ? error.message
          : t('cdrFilters.messages.deleteError'),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deleteModal.filter, refresh, showError, showSuccess, t]);

  const renderActions = useCallback(
    (filter: CdrFilter) => (
      <div className="flex items-center gap-2">
        <SimpleTooltip content={t('common.edit')} position="top">
          <button
            type="button"
            onClick={() => handleEdit(filter)}
            className={`${ACTION_BUTTON_CLASSES.base} ${ACTION_BUTTON_CLASSES.edit}`}
            aria-label={t('common.edit')}
          >
            <Edit className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.duplicate')} position="top">
          <button
            type="button"
            onClick={() => handleDuplicate(filter)}
            className={`${ACTION_BUTTON_CLASSES.base} ${ACTION_BUTTON_CLASSES.duplicate}`}
            aria-label={t('common.duplicate')}
          >
            <Copy className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
          </button>
        </SimpleTooltip>

        <SimpleTooltip content={t('common.delete')} position="top">
          <button
            type="button"
            onClick={() => handleDeleteClick(filter)}
            className={`${ACTION_BUTTON_CLASSES.base} ${ACTION_BUTTON_CLASSES.delete}`}
            aria-label={t('common.delete')}
          >
            <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
          </button>
        </SimpleTooltip>
      </div>
    ),
    [handleDeleteClick, handleDuplicate, handleEdit, t],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          columns={columns}
          loading={loading}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          onAdd={handleAdd}
          title={pageTitle}
          titleIcon={<Filter className="h-5 w-5 text-primary" />}
          addButtonText={t('cdrFilters.actions.add')}
          renderActions={renderActions}
          searchPlaceholder={t('cdrFilters.searchPlaceholder')}
          emptyMessage={t('cdrFilters.emptyMessage')}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, filter: null })}
        onConfirm={handleDeleteConfirm}
        title={t('cdrFilters.messages.deleteTitle')}
        message={t('cdrFilters.messages.confirmDelete', {
          description: deleteModal.filter?.description || '',
        })}
        confirmText={t('cdrFilters.actions.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}

